import * as React from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import Typography from '@mui/joy/Typography';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import emailjs from '@emailjs/browser';
import {
  Section,
  SectionHeading,
  PageHero,
  PrimaryButton,
  InfoTile,
} from '../components/ui';
import usePageMeta from '../hooks/usePageMeta';
import './styles/Join.css';

// Global constants (CAPITALIZED_SNAKE_CASE): stable external contact values.
const PHONE_DISPLAY = '(972) 475-7511';
const PHONE_HREF = 'tel:9724757511';
const EMAIL = 'contactNHV@gmail.com';
const ADDRESS = '4301 Dalrock Road, Rowlett, TX 75088';
const DIRECTIONS_URL = 'https://maps.app.goo.gl/aptJc46XDx7rMGwG7';
const SERVICE_TIME = 'Sundays at 10:45 AM';
const MAP_EMBED_SRC =
  'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d837.4761177952921!2d-96.5196937!3d32.9006939!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864ea9dc8024ae1f%3A0xd7bbc9e917ccf7a!2sNew%20Horizon%20Vineyard!5e0!3m2!1sen!2sus!4v1748026039587!5m2!1sen!2sus';

const Contact = () => {
  usePageMeta(
    'Contact Us',
    'Get in touch with New Horizon Vineyard in Rowlett, TX. Call, email, or send us a ' +
      'message. We’d love to hear from you.',
  );
  // Component-local: the form element ref required by EmailJS sendForm.
  const form = React.useRef();
  // Component-local: tracks submit feedback so we can show a safe status line.
  const [status, setStatus] = React.useState('idle');

  // PRESERVED EmailJS contract: same serviceID / templateID / publicKey and
  // the same input name attributes (from_name, reply_to, message) as ContactModal.
  const sendEmail = (e) => {
    e.preventDefault();
    setStatus('sending');

    emailjs
      .sendForm('hermes_mail', 'contact_email', form.current, {
        publicKey: 'SKtBmx7iHN3engwzt',
      })
      .then(
        () => {
          console.log('[EmailJS] Successfully sent email.');
          setStatus('sent');
          if (form.current) {
            form.current.reset();
          }
        },
        () => {
          console.error('[EmailJS] Error: Failed to send.');
          setStatus('error');
        }
      );
  };

  // Local input focus styling, driven by brand tokens. Per the shared palette
  // spec the focus ring is the orange accent (accent.500 #F58025) at 2px with a
  // 2px offset, so it stays visible against navy and neutral surfaces alike.
  const fieldSx = {
    '&::before': { display: 'none' },
    '&:focus-within': {
      outline: '2px solid var(--joy-palette-accent-500)',
      outlineOffset: '2px',
    },
  };

  return (
    <Box component="main">
      <PageHero
        title="Get In Touch"
        eyebrow="Contact Us"
        subtitle="Have a question, a prayer request, or just want to say hello? We’d love to hear from you."
        height="sm"
        overlay="strong"
      />

      <Section bg="default" padding="md">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 6, md: 8 }}>
          {/* Left: contact details */}
          <Stack spacing={4} sx={{ flex: 1 }}>
            <SectionHeading
              eyebrow="Reach Out"
              title="We’re here to help"
              subtitle="Connect with us through any of the options below, or send a message using the form."
            />
            <Stack spacing={3}>
              <InfoTile
                icon={<PhoneIcon />}
                label="Phone"
                value={PHONE_DISPLAY}
                href={PHONE_HREF}
              />
              <InfoTile
                icon={<EmailIcon />}
                label="Email"
                value={EMAIL}
                href={`mailto:${EMAIL}`}
              />
              <InfoTile
                icon={<PlaceIcon />}
                label="Address"
                value={ADDRESS}
                href={DIRECTIONS_URL}
              />
              <InfoTile
                icon={<AccessTimeIcon />}
                label="Service Time"
                value={SERVICE_TIME}
              />
            </Stack>
            <PrimaryButton href={DIRECTIONS_URL} variant="outlined">
              Get Directions
            </PrimaryButton>
          </Stack>

          {/* Right: inline contact form (mirrors ContactModal's EmailJS contract) */}
          <Box
            sx={{
              flex: 1,
              bgcolor: 'background.level1',
              borderRadius: 'lg',
              boxShadow: 'sm',
              p: { xs: 3, md: 4 },
            }}
          >
            <Typography level="h3" sx={{ color: 'text.primary', mb: 2 }}>
              Send us a message
            </Typography>
            <form ref={form} onSubmit={sendEmail}>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input required sx={fieldSx} name="from_name" />
                </FormControl>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input required type="email" sx={fieldSx} name="reply_to" />
                </FormControl>
                <FormControl>
                  <FormLabel>Message</FormLabel>
                  <Textarea minRows={5} required sx={fieldSx} name="message" />
                </FormControl>
                <PrimaryButton
                  type="submit"
                  fullWidth
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : 'Send Message'}
                </PrimaryButton>
                {status === 'sent' ? (
                  <Typography level="body-sm" sx={{ color: 'primary.600' }}>
                    Thanks! Your message has been sent.
                  </Typography>
                ) : null}
                {status === 'error' ? (
                  <Typography level="body-sm" sx={{ color: 'danger.500' }}>
                    Something went wrong. Please try again or email us directly.
                  </Typography>
                ) : null}
              </Stack>
            </form>
          </Box>
        </Stack>
      </Section>

      {/* Map */}
      <Section bg="tint" padding="md">
        <SectionHeading title="Visit us" align="center" />
        <Box
          component="iframe"
          src={MAP_EMBED_SRC}
          title="NHV-Map"
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          sx={{
            border: 0,
            borderRadius: 'md',
            boxShadow: 'sm',
            width: '100%',
            height: { xs: 300, md: 440 },
          }}
        />
      </Section>
    </Box>
  );
};

export default Contact;
